import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  ConsumerChargeCommercialInterface,
  RequestConsumerChargeCommercialInterface,
} from '../../../../interface/end-consumer-charge-Session-commercial-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setSelectedDevice } from '../../../../redux/slices/DeviceManagementSlice';
import {
  fetchConsumerChargeCommercial,
  fetchConsumerChargeCommercialFilter,
  refreshConsumerChargeCommercial,
  setSortData,
  updateConsumerChargeCommercialListFilter,
} from '../../../../redux/slices/EndConsumerChargeCommercial';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import CustomDateRangePicker from '../../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ChargingSessionSidePanel from '../../../../shared-components/table-side-panel/ChargingSessionSidePanel';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import EmspSessionSidePanel from '../../../../shared-components/table-side-panel/EmspSessionSidePanel';
import { useTableSidePanel } from '../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import LocationSidePanel from '../../../../shared-components/table-side-panel/LocationSidePanel';
import PaymentDetails from '../../../../shared-components/table-side-panel/PaymentDetails';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import {
  convertUtcTo12HourTime,
  getDaysBetween,
  removeEmptyKeys,
} from '../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import FormatDuration from '../../../../shared-functions/FormatDuration';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import emspSessionApi from '../../../../utils/api/v2/emsp-session';
import ActiveChargingSession from '../SidePaneComponents/ActiveChargingSession/ActiveChargingSession';

interface ModelDetailsInterface {
  title?: string;
  content: JSX.Element;
  url?: string;
  urlNewTab?: string;
}

/**
 * Charge Session List
 * @returns {any} Charge Session List
 */
function ChargeSessionCommercialList() {
  const { t, i18n } = useTranslation('endConsumer');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [modelDetails, setModelDetails] = useState<ModelDetailsInterface>({
    title: '',
    content: <div />,
    url: '',
    urlNewTab: '',
  });

  const currentDate = new Date().toISOString().split('T')[0];

  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: currentDate,
    endDate: currentDate,
  });

  /**
   * Clear date range.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Render date filters.
   * @returns {React.JSX.Element} The rendered date filters.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          disableDates="future"
        />
      </div>
    );
  };

  const {
    consumers,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.ConsumerChargeCommercial);
  const { selectedEndConsumer, endConsumerType } = useAppSelector((state) => state.endConsumerType);

  /**
   * Fetch Users list function
   * @param {RequestConsumerChargeCommercialInterface} data - The data
   */
  const fetchData = (data: RequestConsumerChargeCommercialInterface) => {
    if (data.startDate && data.endDate && getDaysBetween(data.startDate, data.endDate) > 31) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (!loading && selectedEndConsumer?.userCustomId) {
      const payload = removeEmptyKeys({
        userCustomId: selectedEndConsumer.userCustomId,
        // status: 'completed',
        ...data,
      });
      dispatch(fetchConsumerChargeCommercial(payload));
    }
  };

  /**
   * Handle open charge session details
   * @param {any} data - The data
   */
  const handleOpenChargeSessionDetails = (data: ConsumerChargeCommercialInterface) => {
    navigate(`?chargingId=${data?.sessionId}`);
    setIsOpen(true);
    setModelDetails({
      title: t('chargingSessionDetails'),
      content: <ChargingSessionSidePanel fetchIdFrom="queryParams" />,
    });
  };

  /**
   * Handle open end consumer details
   * @param {any} rowData - The rowData
   */
  const handleOpenDeviceDetails = (rowData: ConsumerChargeCommercialInterface) => {
    navigate(`?deviceId=${rowData?.chargingPoint?.chargingPointSn}`);
    setIsOpen(true);
    dispatch(
      setSelectedDevice({
        id: rowData?.chargingPoint?._id,
        name: rowData?.chargingPoint?.chargingPointName,
        chargingPointSn: rowData?.chargingPoint?.chargingPointSn,
      }),
    );
    setModelDetails({
      title: '',
      content: <DeviceSidePanel fetchIdFrom="queryParams" />,
      url: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/charger/${rowData?.chargingPoint?._id}/device-details?searchBy=${rowData?.chargingPoint?.chargingPointName}`,
      urlNewTab: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/charger-expanded/${rowData?.chargingPoint?._id}/device-details?searchBy=${rowData?.chargingPoint?.chargingPointName}`,
    });
  };

  /**
   * Handle open active session details
   * @param {any} currentRow - The currentRow
   */
  const handleOpenActiveSessionDetails = (currentRow: ConsumerChargeCommercialInterface) => {
    setIsOpen(true);
    setModelDetails({
      title: 'Live Session',
      content: <ActiveChargingSession dataList={currentRow} consumerType="commercial" />,
    });
  };

  const column = [
    {
      label: t('sessionId') || 'Session Id',
      accessorKey: 'sessionId' as keyof ConsumerChargeCommercialInterface,
      queryParamsKey: 'sessionId',
      queryParamsValue: 'sessionId',
      sidePanelComponent: <EmspSessionSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('sessionStartAt'),
      accessorKey: 'sessionStartAt' as keyof ConsumerChargeCommercialInterface,
      /**
       * Format Session Start Time
       * @param {ConsumerChargeCommercialInterface} rowData - The row data
       * @returns Formatted date element
       */
      customData: (rowData: ConsumerChargeCommercialInterface) => {
        return rowData.sessionStartAt ? (
          <span>{ConvertUTCtoDateTime(rowData.sessionStartAt)}</span>
        ) : (
          <span>-</span>
        );
      },
    },
    {
      label: t('sessionEndsAt'),
      accessorKey: 'sessionEndAt' as keyof ConsumerChargeCommercialInterface,
      /**
       * Format Session End Time
       * @param {ConsumerChargeCommercialInterface} rowData - The row data
       * @returns Formatted date element
       */
      customData: (rowData: ConsumerChargeCommercialInterface) => {
        return rowData.sessionEndAt ? (
          <span>{ConvertUTCtoDateTime(rowData.sessionEndAt)}</span>
        ) : (
          <span>-</span>
        );
      },
    },
    {
      label: t('locationId'),
      accessorKey: 'chargingStationName' as keyof ConsumerChargeCommercialInterface,
      sortAllow: false,
      /**
       * Rendering station name with hyper link
       * @param {any} currentRow - current row data
       * @returns {any} - custom data
       */
      customData: (currentRow: ConsumerChargeCommercialInterface) => (
        <span>{currentRow?.chargingStation?.name || ''}</span>
      ),
    },
    {
      label: t('chargerName'),
      accessorKey: 'chargingPointSn' as keyof ConsumerChargeCommercialInterface,
      /**
       * Rendering device name with hyper link
       * @param {any} currentRow - current row data
       * @returns {any} - custom data
       */
      customData: (currentRow: ConsumerChargeCommercialInterface) => (
        <p role="presentation" onClick={() => handleOpenDeviceDetails(currentRow)}>
          {currentRow.chargingPoint?.chargingPointSn || ''}
        </p>
      ),
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'energyKwh' as keyof ConsumerChargeCommercialInterface,
      sortAllow: true,
      /**
       * KWh Consumed
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: ConsumerChargeCommercialInterface) => (
        <p role="presentation">
          {ToFixedValue(currentRow?.powerConsumedInKwh, 3)} {t('kWh')}
        </p>
      ),
    },
    {
      label: t('transactionAmount'),
      accessorKey: 'totalCost' as keyof ConsumerChargeCommercialInterface,
      sortAllow: true,
      /**
       * Rendering transaction amount
       * @param {ConsumerChargeCommercialInterface} currentRow - current row data
       * @returns {React.JSX.Element} Transaction amount with currency
       */
      customData: (currentRow: ConsumerChargeCommercialInterface): React.JSX.Element => (
        <span>
          {currentRow?.totalCost
            ? `${ToFixedValue(currentRow.totalCost)} ${currentRow.currency}`
            : `0.00 ${currentRow.currency}`}
        </span>
      ),
    },
    {
      label: t('duration'),
      accessorKey: 'durationSeconds' as keyof ConsumerChargeCommercialInterface,
      /**
       * Rendering duration
       * @param {ConsumerChargeCommercialInterface} rowData - The row data
       * @returns Duration
       */
      customData: (rowData: ConsumerChargeCommercialInterface) => {
        return <span>{FormatDuration(rowData.durationSeconds, { formatBy: 'sec' })}</span>;
      },
    },
    {
      label: t('driverInfo'),
      accessorKey: 'driver' as keyof ConsumerChargeCommercialInterface,
      /**
       * Driver Info
       * @param {ConsumerChargeCommercialInterface} rowData - The row data
       * @returns User Custom ID
       */
      customData: (rowData: ConsumerChargeCommercialInterface) => {
        return <span>{rowData.driver?.userCustomId || rowData.userCustomId || '-'}</span>;
      },
    },
    {
      label: t('driverName'),
      accessorKey: 'driver' as keyof ConsumerChargeCommercialInterface,
      /**
       * Rendering driver name
       * @param {ConsumerChargeCommercialInterface} currentRow passing row data as a props
       * @returns Driver full name
       */
      customData: (currentRow: ConsumerChargeCommercialInterface) => (
        <span>
          {currentRow.driver?.firstName} {currentRow.driver?.lastName}
        </span>
      ),
    },

    {
      label: t('vehicle'),
      accessorKey: 'vehicle' as keyof ConsumerChargeCommercialInterface,
      sortAllow: false,
      /**
       * Rendering vehicle name
       * @param {ConsumerChargeCommercialInterface} currentRow prop
       * @returns Returning manufacturer name
       */
      customData: (currentRow: ConsumerChargeCommercialInterface) => (
        <span>
          {currentRow.vehicle.manufacturer} - {currentRow.vehicle.model}
        </span>
      ),
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof ConsumerChargeCommercialInterface,
      sortAllow: false,
      /**
       * Status
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: ConsumerChargeCommercialInterface) => {
        if (currentRow?.status === 'inProgress') {
          return (
            <p
              role="presentation"
              className="hyper-link"
              onClick={() => handleOpenActiveSessionDetails(currentRow)}>
              {t('inProgress')}
            </p>
          );
        }
        return (
          <p
            role="presentation"
            className="hyper-link"
            onClick={() => handleOpenChargeSessionDetails(currentRow)}>
            {currentRow?.status ? t(currentRow.status) : t('na')}
          </p>
        );
      },
    },
  ];

  /**
   * Handle the refresh function via Redux
   */
  const handleAutoRefresh = () => {
    if (!selectedEndConsumer?.userCustomId) return;
    const queryParameters = GetParametersFromCommonFilters(filters);

    const payload = removeEmptyKeys({
      userCustomId: selectedEndConsumer.userCustomId,
      ...queryParameters,
      page,
      limit,
      sortBy,
      sortField,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
    dispatch(refreshConsumerChargeCommercial(payload));
  };

  /**
   * Current columns for Commercial Charging Session export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('sessionId') || 'Session Id', accessorKey: 'sessionId' },
    { label: t('sessionStartAt'), accessorKey: 'sessionStartAt' },
    { label: t('sessionEndsAt'), accessorKey: 'sessionEndAt' },
    { label: t('driverId'), accessorKey: 'userCustomId' },
    { label: t('locationId'), accessorKey: 'chargingStationName' },
    { label: t('chargerName'), accessorKey: 'chargingPointSn' },
    { label: t('powerConsumed'), accessorKey: 'powerConsumedInKwh' },
    { label: t('duration'), accessorKey: 'durationSeconds' },
    { label: t('driverInfo'), accessorKey: 'driverId' },
    { label: t('driverName'), accessorKey: 'driverName' },
    { label: t('vehicle'), accessorKey: 'vehicleName' },
    { label: t('status'), accessorKey: 'status' },
  ];
  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    if (!selectedEndConsumer?.userCustomId) return;

    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload = removeEmptyKeys({
      userCustomId: selectedEndConsumer.userCustomId,
      ...queryParameters,
      sortBy,
      sortField,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });

    emspSessionApi
      .fetchCommercialSessionExport(payload)
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        const rows = response.data.map((row: any, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'sessionId':
                return row?.sessionId || t('na');

              case 'createdAt': {
                const created = row?.sessionStartAt || row?.createdAt;
                return created
                  ? `${ConvertUTCtoDate(created)} ${convertUtcTo12HourTime(created)}`
                  : t('na');
              }

              case 'sessionStartAt':
                return row?.sessionStartAt ? ConvertUTCtoDateTime(row.sessionStartAt) : t('na');

              case 'sessionEndAt':
                return row?.sessionEndAt ? ConvertUTCtoDateTime(row.sessionEndAt) : t('na');

              case 'userCustomId':
                return row?.driver?.userCustomId || row?.userCustomId || t('na');

              case 'durationSeconds':
                return row?.durationSeconds ? FormatDuration(row.durationSeconds) : t('na');

              case 'driverId':
                return row?.driver?.userCustomId || t('na');

              case 'driverName': {
                const fullName =
                  `${row?.driver?.firstName || ''} ${row?.driver?.lastName || ''}`.trim();
                return fullName || t('na');
              }

              case 'chargingStationName':
                return (
                  row?.locationId ||
                  row?.chargingStationName ||
                  row?.chargingStation?.name ||
                  t('na')
                );

              case 'chargingPointSn':
                return (
                  row?.evseUid ||
                  row?.chargingPointSn ||
                  row?.chargingPoint?.chargingPointSn ||
                  t('na')
                );

              case 'powerConsumedInKwh': {
                const power = row?.energyKwh || row?.powerConsumedInKwh;
                return power ? `${ToFixedValue(Number(power), 3)} ${t('kWh')}` : `0 ${t('kWh')}`;
              }
              case 'status':
                return row?.status ? t(row.status) : t('na');

              case 'vehicleName': {
                const vName =
                  row?.driver?.vehicle?.manufacturer ||
                  row?.vehicleName ||
                  row?.vehicle?.manufacturer;
                return vName || t('na');
              }

              case 'sessionType':
                if (row?.sessionType === 'FREE') {
                  return t('guest');
                }
                return row?.sessionType ? t(row.sessionType) : t('na');

              case 'paymentMethod': {
                let pMethod = t('na');

                if (row?.paymentMethod === 'wallet') {
                  pMethod = t('wallet');
                } else if (
                  row?.paymentMethod === 'hyperpay' ||
                  row?.paymentMethod === 'credit card'
                ) {
                  pMethod = t('creditCard');
                } else if (row?.paymentMethod === 'checkoutdotcom') {
                  pMethod = t('checkoutDotCom');
                } else if (row?.paymentMethod) {
                  pMethod = t(row.paymentMethod);
                }

                return pMethod;
              }

              case 'hyperpayTransactionObjectId':
                return (
                  row?.transactionId ||
                  row?.hyperpayTransactionId ||
                  row?.hyperpayTransaction?.hyperpayTransactionId ||
                  t('na')
                );

              case 'amount': {
                const amount = row?.totalCost || row?.amount || row?.walletHistoryObject?.amount;
                return amount ? `${ToFixedValue(amount)} ${t('SAR')}` : `0.00 ${t('SAR')}`;
              }

              default:
                return t('na');
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'end-consumer-charging-session-commercial.csv');
          } else {
            exportPDF(headers, rows, 'end-consumer-charging-session-commercial.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error: any) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Table pagination function
   * @param {any} value - pagination value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      ...value,
      sortField,
      sortBy,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      sortField,
      sortBy,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  const { handleSidePanelClose, renderSidePanelTopLeftContent } = useTableSidePanel({
    onRefresh: handleRefresh,
  });

  /**
   * Function to close modal
   */
  const handleCloseModal = () => {
    setIsOpen(false);
    setModelDetails({ title: '', content: <div /> });
    setSearchParameters({});
  };

  const previousDateRange = React.useRef({ startDate: '', endDate: '' });

  useEffect(() => {
    if (!selectedEndConsumer?.userCustomId) return;

    if (
      previousDateRange.current.startDate !== dateRange.startDate ||
      previousDateRange.current.endDate !== dateRange.endDate
    ) {
      previousDateRange.current = dateRange;
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
    }
  }, [dateRange.startDate, dateRange.endDate, filters, limit, selectedEndConsumer]);

  useEffect(() => {
    if (!selectedEndConsumer?.userCustomId) return;

    const currentId = selectedEndConsumer.userCustomId;
    const lastId = sessionStorage.getItem('chargeSessionCommercial');

    if (isFilterUpdated || consumers.length === 0 || lastId !== currentId) {
      if (selectedEndConsumer?.userCustomId) {
        const queryParameters = GetParametersFromCommonFilters(filters);
        fetchData({
          page: DefaultApiLimit.page,
          limit,
          ...queryParameters,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        });
      }
    }
    sessionStorage.setItem('chargeSessionCommercial', currentId);
  }, [
    filters,
    selectedEndConsumer,
    isFilterUpdated,
    consumers.length,
    dateRange.startDate,
    dateRange.endDate,
    limit,
  ]);

  useEffect(() => {
    const stationId = searchParameters.get('stationId');
    // const deviceId = searchParameters.get('deviceId');
    const chargingId = searchParameters.get('chargingId');
    const paymentId = searchParameters.get('paymentId');

    if (stationId) {
      setIsOpen(true);
      setModelDetails({
        title: '',
        content: <LocationSidePanel fetchIdFrom="queryParams" />,
        url: `/ev-admin/station-management/${stationId}/station-dashboard?source=${location.pathname}`,
      });
    }
    //  else if (deviceId) {
    //   setIsOpen(true);
    //   setModelDetails({
    //     title: '',
    //     content: <DeviceSidePanel fetchIdFrom="queryParams" />,
    //     url: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/device/${deviceId}/device-details`,
    //   });
    // }
    else if (chargingId) {
      setIsOpen(true);
      setModelDetails({
        title: t('chargingSessionDetails'),
        content: <ChargingSessionSidePanel fetchIdFrom="queryParams" />,
      });
    } else if (paymentId) {
      setIsOpen(true);
      setModelDetails({
        title: t('paymentDetails'),
        content: <PaymentDetails fetchIdFrom="queryParams" />,
      });
    }
  }, [searchParameters, selectedEndConsumer, endConsumerType]);

  return (
    <div className="charging-session-wrap" dir={i18n.dir()}>
      <SidePaneModel
        open={isOpen}
        title={modelDetails.title}
        content={modelDetails.content}
        closePanel={handleCloseModal}
        url={modelDetails.url}
        urlNewTab={modelDetails.urlNewTab}
        preventQueryClear
      />
      <CustomTable
        header={column}
        data={consumers}
        serialNo
        actionButton
        sizeChangeButtonRequired
        version="v2"
        height={500}
        page={page}
        limit={limit}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        filters={filters}
        filtersToDispatch={updateConsumerChargeCommercialListFilter}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchConsumerChargeCommercialFilter}
        exportData={consumers}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        refreshRequired
        requiredRefreshToggle
        refershCurrentPages={handleRefresh}
        refreshFunction={handleAutoRefresh}
        refreshInterval={10}
        rightTopContent={<div className="table-bottom-button-list">{renderDateFilters()}</div>}
        onClearFilterChange={() => {
          clearDateRange();
        }}
        leftTopContent={<> </>}
        expandTableOpenKey="ChargeSessionCommercialV2ListEndConsumer"
        expandTableOpen
        autoRefreshEnabled
        onHiddenColumnsChange={setHiddenColumns}
        sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
        onSidePanelClose={handleSidePanelClose}
      />{' '}
    </div>
  );
}

export default ChargeSessionCommercialList;
